import Tile from '../subcomponents/Tile';
import './styles/board.css';
import { useGameStore } from '../../store/gameStore';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { getBonusLetter, getNextLetter } from '../utils/nextLetterHelper';
import { applyFlipAnimation, applySwapCounterAnimation } from '../utils/animationHelper';
import { checkForWords, cloneBoard, foundWordDelay } from '../utils/boardHelper';
import { useShallow } from 'zustand/react/shallow';

interface BoardProps {
  setShowGameOver: Dispatch<SetStateAction<boolean>>;
}

function Board(props: BoardProps) {
  const { setShowGameOver } = props;

  const bonusLetter = getBonusLetter();

  // Subscribe to game store
  const {
    mode,
    board,
    moveCount,
    gameEnded,
    pauseActions,
    updateBoard,
    increaseMoveCount,
    decreaseSwapCount,
    addAction,
  } = useGameStore(
    useShallow((state) => ({
      mode: state.mode,
      board: state.gameData[state.mode].board,
      moveCount: state.gameData[state.mode].moveCount,
      gameEnded: state.gameData[state.mode].gameEnded,
      pauseActions: state.pauseActions,
      updateBoard: state.updateBoard,
      increaseMoveCount: state.increaseMoveCount,
      decreaseSwapCount: state.decreaseSwapCount,
      addAction: state.addAction,
    }))
  );

  const [canFlip, setCanFlip] = useState<boolean>(true);
  const [nextLetter, setNextLetter] = useState<string>(getNextLetter(mode, moveCount));

  const isBonusLetter = (letter: string) => {
    return letter === bonusLetter;
  };

  const isLargeTile = () => {
    return mode.includes('Simple');
  };

  const resetCanFlip = (isWordFound: boolean) => {
    const delay: number = isWordFound ? foundWordDelay : 250;
    setTimeout(() => {
      setCanFlip(true);
    }, delay + 5);
  };

  const saveAction = (action: { row: number; col: number }) => {
    addAction(action);
  };

  const handleTileClick = (rowIdx: number, colIdx: number, prevLetter: string) => {
    if (!canFlip || pauseActions) return;
    if (gameEnded) {
      setShowGameOver(true);
      return;
    }

    setCanFlip(false);

    const newBoard = cloneBoard(board);
    newBoard[rowIdx][colIdx] = nextLetter;

    const tile = document.getElementById(`${rowIdx}-${colIdx}`);
    applyFlipAnimation(tile);

    updateBoard(newBoard);
    increaseMoveCount();

    const isWordFound = checkForWords(cloneBoard(newBoard), rowIdx, colIdx);

    if (!isWordFound && prevLetter !== '') {
      decreaseSwapCount();
      applySwapCounterAnimation();
    }

    // Save every move action to analyze for leaderboard
    const action = { row: rowIdx, col: colIdx };
    saveAction(action);

    resetCanFlip(isWordFound);
  };

  // Handle next letter after each move
  useEffect(() => {
    setNextLetter(getNextLetter(mode, moveCount));
  }, [moveCount]);

  return (
    <div className="board">
      {board.map((row, rowIdx) => (
        <div key={rowIdx}>
          {row.map((letter, colIdx) => (
            <Tile
              value={letter}
              id={`${rowIdx}-${colIdx}`}
              key={`${rowIdx}-${colIdx}`}
              bonus={isBonusLetter(letter)}
              large={isLargeTile()}
              onClick={() => handleTileClick(rowIdx, colIdx, letter)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export default Board;
